import axios from 'axios'
import { formatErrorMessage } from '../utils/validate'
import { toastError } from '../utils/toast'

const ACCESS_TOKEN_KEY = 'psyscale_access_token'
const REFRESH_TOKEN_KEY = 'psyscale_refresh_token'

export function getAccessToken() {
  return window.localStorage.getItem(ACCESS_TOKEN_KEY) || ''
}

export function getRefreshToken() {
  return window.localStorage.getItem(REFRESH_TOKEN_KEY) || ''
}

export function setTokens({ access, refresh }) {
  if (access) window.localStorage.setItem(ACCESS_TOKEN_KEY, access)
  if (refresh) window.localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
}

export function clearTokens() {
  window.localStorage.removeItem(ACCESS_TOKEN_KEY)
  window.localStorage.removeItem(REFRESH_TOKEN_KEY)
}

const baseURL = process.env.VUE_APP_API_BASE_URL || '/api'


const http = axios.create({
  baseURL,
  timeout: 30000,
})

let isRefreshing = false
let pendingQueue = []

function redirectToLoginIfNeeded() {
  // 仅在需要鉴权的接口失败时才会走到这里（401）
  // 使用 hash 路由，因此直接修改 location.hash 即可跳转
  if (window.location.hash.startsWith('#/public/')) return
  if (window.location.hash.startsWith('#/login')) return
  window.location.hash = '#/login'
}

function flushQueue(error, accessToken) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(accessToken)
  })
  pendingQueue = []
}

function shouldToast(meta, status) {
  if (!meta) return true
  if (meta.silent) return false
  if (meta.toast === false) return false
  const toastOn = meta.toastOn
  if (!toastOn) return true
  if (!status) return false
  if (Array.isArray(toastOn)) {
    if (toastOn.includes(status)) return true
    if (status >= 500 && toastOn.includes('5xx')) return true
    return false
  }
  return true
}

http.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

http.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {}
    const status = error.response && error.response.status
    const data = (error.response && error.response.data) || {}
    const meta = original.meta || {}
    const allowDuplicateToast = !!meta.allowDuplicateToast

    // 统一错误提示逻辑
    // 5xx: 服务器错误，统一提示
    if (status >= 500 && shouldToast(meta, status)) {
      toastError('服务器内部错误，请联系管理员', { dedupeMs: allowDuplicateToast ? 0 : 2000 })
      error.isHandled = true
    }
    // 403: 权限不足
    else if (status === 403 && shouldToast(meta, status)) {
      toastError(data.detail || '您没有权限执行此操作', { dedupeMs: allowDuplicateToast ? 0 : 2000 })
      error.isHandled = true
    }
    // 404: 资源不存在 (有时 API 会返回 404，提示一下比较好)
    else if (status === 404 && shouldToast(meta, status)) {
      toastError('请求的资源未找到', { dedupeMs: allowDuplicateToast ? 0 : 2000 })
      error.isHandled = true
    }
    // 400: 请求参数错误
    // 如果是 detail 字段（通用错误），直接提示
    // 如果是字段验证错误（对象形式），通常交给组件处理（表单高亮等），但为了防止组件漏处理，这里也可以酌情提示
    else if (status === 400 && shouldToast(meta, status)) {
      if (data.detail) {
        toastError(data.detail, { dedupeMs: allowDuplicateToast ? 0 : 2000 })
        error.isHandled = true
      } else if (meta.handle400 === 'global') {
        toastError(formatErrorMessage(error) || '请求参数错误', { dedupeMs: allowDuplicateToast ? 0 : 2000 })
        error.isHandled = true
      }
    }

    if (status !== 401) {
      return Promise.reject(error)
    }

    if (original.__isRetryAfterRefresh) {
      clearTokens()
      return Promise.reject(error)
    }

    const refresh = getRefreshToken()
    if (!refresh) {
      clearTokens()
      redirectToLoginIfNeeded()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({
          resolve: (token) => {
            original.__isRetryAfterRefresh = true
            original.headers = original.headers || {}
            original.headers.Authorization = `Bearer ${token}`
            resolve(http(original))
          },
          reject,
        })
      })
    }

    isRefreshing = true
    try {
      // 拼接 baseURL，确保刷新 token 的请求也走配置的地址
      const refreshUrl = `${baseURL.replace(/\/+$/, '')}/users/refresh/`
      const resp = await axios.post(refreshUrl, { refresh })
      const access = resp.data && resp.data.access
      if (!access) throw new Error('刷新 token 失败')
      setTokens({ access })
      flushQueue(null, access)
      original.__isRetryAfterRefresh = true
      original.headers = original.headers || {}
      original.headers.Authorization = `Bearer ${access}`
      return http(original)
    } catch (e) {
      flushQueue(e, null)
      clearTokens()
      redirectToLoginIfNeeded()
      return Promise.reject(e)
    } finally {
      isRefreshing = false
    }
  }
)

export default http
